import React, { ChangeEvent, useCallback } from "react";
import { MiInputSelectBox } from "@miplatform/components";
import {
  AccountType,
  IAccountResponse,
  ICommunitiesResponse,
} from "@miplatform/types";
import { ServerAutocomplete } from "@mibuildweb/components";
import { useAccounts, useCommunities } from "@mibuildweb/hooks";
import { Box, Divider, Typography } from "@mui/material";

export type optionsType = { label: string; value: string };

export type Filters = {
  groupBy: string;
  sortBy: string;
  accountId: string;
  communityId: string;
};

export const groupByOptions = [
  { label: "Customer", value: "billToAccountId" },
  { label: "Invoice Date", value: "invoicedDate" },
];

export const sortByOptions = [
  { label: "Invoice Date", value: "invoicedDate" },
  { label: "Amount Due", value: "amountDue" },
  { label: "Original Amount", value: "totalAmount" },
];

type FilterComponentProps = {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
};

export const FilterComponent: React.FC<FilterComponentProps> = ({
  filters,
  setFilters,
}) => {
  const { communities, getCommunities } = useCommunities();
  const { accounts, getAccounts } = useAccounts({
    accountTypes: [AccountType.Builder, AccountType.Service],
  });
  const handleGroupByChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({
        ...prev,
        groupBy:
          event.target.value.toLowerCase() === "none" ? "" : event.target.value,
      }));
    },
    [setFilters]
  );

  const handleSortChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({
        ...prev,
        sortBy: event.target.value,
      }));
    },
    [setFilters]
  );

  const handleCustomerChange = useCallback((value: string | null) => {
    setFilters((prevFilters: Filters) => ({
      ...prevFilters,
      accountId: value || "",
    }));
  }, []);

  const handleCommunityChange = useCallback((value: string | null) => {
    setFilters((prevFilters: Filters) => ({
      ...prevFilters,
      communityId: value || "",
    }));
  }, []);

  return (
    <Box height="100%" flex={0.2}>
      <Typography variant="body2" fontWeight={600}>
        Filter & Sort
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box>
        <Typography fontSize={12} fontWeight={600} my={2}>
          Group By
        </Typography>
        <MiInputSelectBox
          options={groupByOptions}
          value={filters.groupBy}
          handleChange={(event: ChangeEvent<HTMLSelectElement>) =>
            handleGroupByChange(event)
          }
        />
        <Typography fontSize={12} fontWeight={600} my={2}>
          Sort By
        </Typography>
        <MiInputSelectBox
          options={sortByOptions}
          value={filters.sortBy}
          disableNone
          handleChange={handleSortChange}
        />
        <Typography fontSize={12} fontWeight={600} my={2}>
          Customer
        </Typography>
        <ServerAutocomplete<IAccountResponse>
          name="customerOptions"
          value={accounts.find((opt) => opt.id === filters.accountId) || null}
          labelKey="name"
          filterKeys={["name"]}
          options={accounts}
          getter={getAccounts}
          onChange={handleCustomerChange}
        />
        <Typography fontSize={12} fontWeight={600} my={2}>
          Community
        </Typography>
        <ServerAutocomplete<ICommunitiesResponse>
          name="communityOptions"
          value={
            communities.find((opt) => opt.id === filters.communityId) || null
          }
          labelKey="name"
          filterKeys={["name"]}
          options={communities}
          getter={getCommunities}
          onChange={handleCommunityChange}
        />
      </Box>
    </Box>
  );
};
